import type { MasterDataApplyCounts } from '../../../domain/ports/CatalogDownloadPort.ts';

export type CatalogDownloadNotificationLevel = 'success' | 'info' | 'warning';

export type CatalogDownloadOutcomeNotification = {
  level: CatalogDownloadNotificationLevel;
  message: string;
};

/** True when apply ran but every master-data array was empty or rows were dropped at parse. */
export function isCatalogDownloadEmptyPayload(data: MasterDataApplyCounts): boolean {
  if (data.skippedDuplicateRevision) return false;
  return (
    data.comunasEscan === 0 &&
    data.participantes === 0 &&
    data.proveedores === 0 &&
    data.usuarios === 0 &&
    data.puntosEntregaCatalogo === 0
  );
}

/** User-facing success lines after a catalog download apply. */
export function buildCatalogDownloadSuccessMessage(data: MasterDataApplyCounts): string {
  const base = `Catálogos actualizados: ${data.comunasEscan} comuna(s), ${data.participantes} participante(s), ${data.proveedores} proveedor(es), ${data.usuarios} usuario(s), ${data.puntosEntregaCatalogo} punto(s) de entrega.`;
  const parts = [base];
  if (data.puntosEntregaCatalogo > 0) {
    parts.push(
      'En comunas con puntos del servidor, el formulario de entrega usa ese catálogo (los lugares locales dejan de listarse en esa comuna).',
    );
  }
  return parts.join(' ');
}

/** Toast copy for manual or startup catalog download outcomes. */
export function buildCatalogDownloadOutcomeNotification(
  data: MasterDataApplyCounts,
  options?: { forcedApply?: boolean },
): CatalogDownloadOutcomeNotification {
  if (data.skippedDuplicateRevision && options?.forcedApply !== true) {
    return {
      level: 'info',
      message:
        'Catálogos ya estaban actualizados (misma revisión del servidor); no se modificó la base local. Si necesita refrescar igual, use «Forzar descarga».',
    };
  }

  if (isCatalogDownloadEmptyPayload(data)) {
    return {
      level: 'warning',
      message:
        'Descarga completada, pero el servidor no aportó filas aplicables (arrays vacíos o filas descartadas por campos obligatorios faltantes). Verifique la respuesta del API.',
    };
  }

  let message = buildCatalogDownloadSuccessMessage(data);
  if (options?.forcedApply) {
    message = `Descarga forzada aplicada. ${message}`;
  }
  return { level: 'success', message };
}
